<script setup lang="ts">
import { ref } from 'vue';
import { useAuth } from '../composables/useAuth';
import IconLogo from "../components/icons/IconLogo.vue";
import { Carousel, Slide, Pagination } from 'vue3-carousel';
import 'vue3-carousel/dist/carousel.css';

const config = {
  itemsToShow: 1,
  wrapAround: false,
  transition: 500,
};

const firstName = ref('');
const lastName = ref('');
const email = ref('');
const password = ref('');
const companyName = ref('');
const mobile = ref('');
const { register, errorMessage } = useAuth();

const firstNameError = ref('');
const lastNameError = ref('');
const emailError = ref('');
const passwordError = ref('');
const companyNameError = ref('');
const mobileError = ref('');

const validateEmail = (email: string) => {
  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailPattern.test(email);
};

const validatePassword = (password: string) => {
  const passwordPattern = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{6,}$/;
  return passwordPattern.test(password);
};

const validateMobile = (mobile: string) => {
  const mobilePattern = /^[0-9]{10}$/;
  return mobilePattern.test(mobile);
};

const handleSubmit = async () => {
  firstNameError.value = '';
  lastNameError.value = '';
  emailError.value = '';
  passwordError.value = '';
  companyNameError.value = '';
  mobileError.value = '';

  if (!firstName.value) {
    firstNameError.value = 'First name is required';
  }

  if (!lastName.value) {
    lastNameError.value = 'Last name is required';
  }

  if (!validateEmail(email.value)) {
    emailError.value = 'Invalid email address';
  }

  if (!validatePassword(password.value)) {
    passwordError.value = 'Password must be at least 6 characters long and include uppercase, lowercase, number, and special character';
  }

  if (!companyName.value) {
    companyNameError.value = 'Company name is required';
  }

  if (!validateMobile(mobile.value)) {
    mobileError.value = 'Mobile number must be 10 digits';
  }

  if (!firstNameError.value && !lastNameError.value && !emailError.value && !passwordError.value && !companyNameError.value && !mobileError.value) {
    await register(firstName.value, lastName.value, email.value, password.value, companyName.value, mobile.value);
  }
};
</script>

<template>
  <body>
  <div class="login-container">
    <div class="content-wrapper">
      <section class="left-section">
        <div class="brand-container">
          <div class="hero-section">
            <img style="width: 138px;height: 34px;margin-top: 40px;margin-bottom: 40px" src="/assets/vertical_logo.png" alt="vertical_logo">
            <h1 class="hero-title">Samen de toekomst vormgeven met data</h1>
            <p class="hero-description">Met data bouwen we aan de toekomst. Slimmer, sneller en sterker – samen transformeren we personenvervoer en tillen we jouw organisatie naar het volgende niveau.</p>
          </div>
          <div class="hero-section">
            <p class="hero-description">Aangedreven door:</p>
            <div class="d-flex flex-row" style="margin-top: 36px">
              <img style="width: 162px;height: 39px" src="/assets/opticus_logo.png" alt="opticus_logo">
              <IconLogo style="width: 116px;height: 48px" alt="Company Logo" class="brand-logo"/>
            </div>
          </div>
        </div>
      </section>
      <section class="right-section">
        <div class="login-form-container">
          <h2 class="login-title">Aangedreven door:</h2>
          <div class="signup-prompt">
            <span class="signup-text">Heb je een account?</span>
            <router-link :to="{ name: 'login' }" class="signup-link">Inloggen</router-link>
          </div>
          <form class="form-fields" @submit.prevent="handleSubmit">
            <div class="input-group">
              <div class="d-flex flex-row align-content-lg-space-between">
                <v-text-field
                    class="mr-8"
                    v-model="firstName"
                    variant="outlined"
                    label="Voornaam"
                    :error-messages="firstNameError"
                ></v-text-field >
                <v-text-field
                    v-model="lastName"
                    variant="outlined"
                    label="Achternaam"
                    :error-messages="lastNameError"
                ></v-text-field>
              </div>
              <v-text-field
                  v-model="companyName"
                  variant="outlined"
                  label="Naam taxibedrijf"
                  :error-messages="companyNameError"
                  style="margin-top: 8px"
              ></v-text-field>
              <v-text-field
                  v-model="mobile"
                  variant="outlined"
                  label="Contactnummer"
                  :error-messages="mobileError"
                  style="margin-top: 8px"
              ></v-text-field>
              <v-text-field
                  v-model="email"
                  variant="outlined"
                  label="Email"
                  :error-messages="emailError"
                  style="margin-top: 8px"
              ></v-text-field>
              <v-text-field
                  v-model="password"
                  variant="outlined"
                  type="password"
                  persistent-hint
                  label="Stel wachtwoord in"
                  :error-messages="passwordError"
                  style="margin-top: 8px"
              ></v-text-field>
              <button type="submit" class="login-button">Account aanmaken</button>
            </div>
          </form>
          <div v-if="errorMessage" class="error-message">{{ errorMessage }}</div>
<!--          <div class="social-login">-->
<!--            <p class="social-divider">Or Continue with</p>-->
<!--            <div class="social-buttons">-->
<!--              <button class="facebook-button">-->
<!--                <img src="/assets/facebook_logo.svg" alt="Facebook Icon" class="social-icon"/>-->
<!--                <span>Facebook</span>-->
<!--              </button>-->
<!--              <button class="google-button">-->
<!--                <img src="/assets/google_logo.svg" alt="Google Icon" class="social-icon"/>-->
<!--                <span>Google</span>-->
<!--              </button>-->
<!--            </div>-->
<!--          </div>-->
        </div>
      </section>
    </div>
  </div>
  </body>
</template>
<style>
body {
  margin: 0;
  padding: 0;
  background-color: rgba(237, 239, 247, 1);
}

.login-container {
  padding: 16px 80px 16px 16px;
}

.content-wrapper {
  gap: 20px;
  display: flex;
}

.left-section {
  display: flex;
  height: 95vh;
  flex-direction: column;
  line-height: normal;
  border-radius: 16px;
  background: linear-gradient(90deg, #EEEEF6 0%, #F8F8FC 100%);
  width: 40%;
}

.brand-container {
  border-radius: 16px;
  display: flex;
  flex-grow: 1;
  padding-top: 32px;
  flex-direction: column;
  overflow: hidden;
  justify-content: space-between; /* Add this line to distribute space evenly */
}

.brand-logo {
  aspect-ratio: 2.4;
  object-fit: contain;
  object-position: center;
  width: 151px;
  margin-left: 40px;
}

.hero-section {
  display: flex;
  flex-direction: column;
  position: relative;
  padding: 8px 40px 20px;
}

.hero-image {
  position: absolute;
  inset: 0;
  height: 100%;
  width: 100%;
  object-fit: cover;
  object-position: center;
}

.hero-title {
  position: relative;
  color: var(--Neutral-5, #191a1f);
  font: 600 24px/34px Montserrat, sans-serif;
}

.hero-description {
  position: relative;
  color: var(--Neutral-5, #191a1f);
  margin-top: 16px;
  font: 400 16px/24px Montserrat, sans-serif;
}


.testimonial-card {
  position: relative;
  width: 40vw;
  border-radius: 16px;
  background: var(--Neutral-6, #c7c8cc);
  display: flex;
  margin-top: 5vh;
  flex-direction: column;
  overflow: hidden;
  font-family: Montserrat, sans-serif;
  color: var(--Neutral-5, #191a1f);
  padding: 16px;
}

.testimonial-text {
  font: 400 14px/20px Montserrat, sans-serif;
}

.testimonial-author {
  display: flex;
  margin-top: 24px;
  gap: 8px;
}

.author-avatar {
  aspect-ratio: 1;
  object-fit: contain;
  object-position: center;
  width: 54px;
  border-radius: 8px;
}

.author-info {
  display: flex;
  flex-direction: column;
  margin: auto 0;
}

.author-name {
  font: 500 12px/1 Montserrat, sans-serif;
}

.author-title {
  font: 400 10px/18px Montserrat, sans-serif;
  margin-top: 4px;
}

.right-section {
  display: flex;
  flex-direction: column;
  line-height: normal;
  width: 60%;
  margin-left: 20px;
}

.login-form-container {
  display: flex;
  margin-top: 48px;
  width: 70%;
  flex-direction: column;
}

.login-title {
  color: var(--Neutral-5, #191a1f);
  text-align: center;
  font: 700 28px Montserrat, sans-serif;
}

.signup-prompt {
  display: flex;
  margin-top: 8px;
  align-items: center;
  justify-content: center;
  font: 500 12px/1 Montserrat, sans-serif;
}

.signup-text {
  color: var(--Neutral-5, #191a1f);
}

.signup-link {
  border-radius: 8px;
  color: var(--blue-3, #2590f4);
  padding: 4px;
  cursor: pointer;
}

.form-fields {
  display: flex;
  margin-top: 24px;
  width: 100%;
  flex-direction: column;
}

.input-group {
  display: flex;
  width: 100%;
  flex-direction: column;
}

.input-field {
  align-items: center;
  border-radius: 8px;
  border: 1px solid var(--Neutral-6, #c7c8cc);
  display: flex;
  min-height: 46px;
  width: 100%;
  gap: 8px;
  padding: 4px 8px;
}

.input-wrapper {
  display: flex;
  min-width: 240px;
  width: 100%;
  flex-direction: column;
  justify-content: center;
  flex: 1;
}

.input-label {
  color: var(--Neutral-8, #595c69);
  font: 500 12px/1 Montserrat, sans-serif;
}

.input-text {
  color: var(--Neutral-5, #191a1f);
  font: 400 14px/1 Montserrat, sans-serif;
}

.password-field {
  margin-top: 8px;
}

.reset-password {
  align-self: end;
  border-radius: 8px;
  margin-top: 8px;
  color: var(--blue-3, #2590f4);
  font: 500 12px/1 Montserrat, sans-serif;
  padding: 4px;
  cursor: pointer;
}

.login-button {
  border-radius: 8px;
  background: var(--Primary-Primary, #080aed);
  margin-top: 8px;
  margin-bottom: 16px;
  width: 100%;
  color: #fcfcfc !important;
  font: 700 14px/24px Montserrat, sans-serif;
  padding: 8px 12px;
  cursor: pointer;
  border: none;
}

.social-login {
  display: flex;
  margin-top: 40px;
  width: 100%;
  flex-direction: column;
}

.social-divider {
  color: var(--Neutral-5, #191a1f);
  text-align: center;
  font: 400 14px/1 Montserrat, sans-serif;
}

.social-buttons {
  display: flex;
  margin-top: 8px;
  width: 100%;
  align-items: center;
  gap: 24px;
}

.facebook-button {
  border-radius: 10px;
  background-color: rgba(24, 119, 242, 1);
  display: flex;
  align-items: center;
  gap: 16px;
  font: 700 16px Helvetica, sans-serif;
  color: rgba(255, 255, 255, 1);
  justify-content: center;
  flex: 1;
  padding: 8px 16px;
  cursor: pointer;
  border: none;
}

.google-button {
  border-radius: 10px;
  background-color: rgba(255, 255, 255, 1);
  display: flex;
  align-items: center;
  gap: 16px;
  font: 500 16px Roboto, sans-serif;
  color: rgba(0, 0, 0, 0.54);
  justify-content: center;
  flex: 1;
  padding: 8px 16px;
  cursor: pointer;
  border: 1px solid #ccc;
}

.social-icon {
  width: 24px;
  height: 24px;
}

.visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  border: 0;
}

@media (max-width: 991px) {
  .login-container {
    padding-right: 20px;
  }

  .content-wrapper {
    flex-direction: column;
    align-items: stretch;
    gap: 0;
  }

  .left-section,
  .right-section {
    width: 100%;
    margin-left: 0;
  }

  .brand-container {
    max-width: 100%;
    margin-top: 40px;
  }

  .brand-logo {
    margin-left: 10px;
  }

  .hero-section {
    max-width: 100%;
    margin-top: 40px;
    padding: 0 20px;
  }

  .hero-description,
  .testimonial-card {
    width: 80vw;
    max-width: 100%;
  }

  .testimonial-card {
    width: 80vw;
    margin: 40px 10px 0 0;
  }

  .login-form-container {
    max-width: 100%;
    margin-top: 40px;
  }

  .social-login {
    max-width: 100%;
    margin-top: 40px;
  }
}
</style>
